<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.save'), type: 'confirm', disabled: isDisabled, callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full p-2 flex flex-col"
  >
    <div v-if="purchaseOrder" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ $t('purchaseOrder.title', { name: purchaseOrder.supplier.name, number: purchaseOrder.purchaseOrderNumber }) }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300">
          {{ $t('purchaseOrder.editProduct') }}
        </p>
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <o-scroll class="py-4 px-8 h-full">
          <o-form ref="form" class="h-full">
            <o-description-details :term="$t('general.employee')">
              <employee-select
                v-model="_employee"
                :types="['employee']"
                class="w-full"
              />
            </o-description-details>

            <o-description-details :term="$t('general.date')" date>
              <date-field
                v-model="purchaseOrder.date"
                :augmenter="Augmenters.timestamp"
                class="w-full"
              />
            </o-description-details>

            <o-description-details :term="$t('general.notes')">
              <text-area-field
                v-model="purchaseOrder.notes"
                class="w-full"
              />
            </o-description-details>

            <o-description-details :term="$t('purchaseOrder.fromAddress')">
              <text-area-field
                v-model="purchaseOrder.addressFrom"
                class="w-full"
              />
            </o-description-details>

            <o-description-details :term="$t('purchaseOrder.toAddress')">
              <text-area-field
                v-model="purchaseOrder.addressTo"
                class="w-full"
              />
            </o-description-details>

            <o-description-details :term="$t('purchaseOrder.shippingAddress')">
              <text-area-field
                v-model="purchaseOrder.addressShip"
                class="w-full"
              />
            </o-description-details>
          </o-form>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import EmployeeSelect from '@/components/employee/elements/Select.vue'
import { ODescriptionDetails, OFieldGroup, OForm } from '@/components/elements'
import { DateField, TextAreaField } from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import type { PurchaseOrder } from '~/_api/models/purchaseOrder'

interface Props {
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'update:purchaseOrder', purchaseOrder: any): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const modal = ref<any>()
const form = ref<any>()
const purchaseOrder = ref<PurchaseOrder | null>(null)

const _employee = computed({
  get () {
    return (purchaseOrder.value as any)?.employee
  },
  set (employee: any) {
    if (purchaseOrder.value) {
      (purchaseOrder.value as any).employee = employee
      ;(purchaseOrder.value as any).idEmployee = employee.idEmployee
    }
  },
})

const taxOptions = computed(() => {
  return []
})

const isDisabled = computed(() => {
  return false
})

const open = (po: PurchaseOrder) => {
  purchaseOrder.value = (po as any).copy()

  modal.value.open()
}

const validate = () => {
  return form.value.validate()
}

const handleConfirm = async () => {
  if (!purchaseOrder.value) return

  emit('update:loading', true)

  try {
    const response = await $api.purchaseOrder.save(purchaseOrder.value)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('general.saved'),
        text: $i18n.t('components.purchaseOrder.actions.details.successInfo'),
        variant: 'success',
      })

      emit('success')
      emit('update:purchaseOrder', response.data)

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })

    emit('error')
  }
}

defineExpose({ open })
</script>
